import { useEffect } from 'react'
import { MyButton } from '@interstellar-component'
import { useApp } from '@src/AppContext'
import { Access } from '@src/services/Helper'
import { CONSENT_SETTINGS } from '@src/services/consentSettings'
import { useSettings } from '../Context'

/** Vertical radio group matching the "Consent for candidate" Figma design. */
function RadioField({ label, name, value, options, onChange, disabled }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-[#344054]">
        {label} <span className="text-[#14378E]">*</span>
      </label>
      <div className="flex flex-col gap-3">
        {options.map((opt) => (
          <label
            key={String(opt.value)}
            className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <input
              type="radio"
              name={name}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              disabled={disabled}
              className="h-4 w-4 accent-[#14378E] disabled:cursor-not-allowed"
            />
            <span className="text-sm font-medium text-[#344054]">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

export default function GeneralSettings() {
  const { hasPermission } = useApp()
  const {
    sessionTimeout,
    setSessionTimeout,
    ektpUploadProcess,
    setEktpUploadProcess,
    kycEnabled,
    setKycEnabled,
    jobApplicationFormMode,
    setJobApplicationFormMode,
    termsConditionMode,
    setTermsConditionMode,
    isLoadingGeneral,
    fetchGeneralSettings,
    updateGeneralSettings,
    cancelGeneralSettings,
  } = useSettings()

  const canEdit = hasPermission(Access.GENERAL_SETTINGS, 'edit')
  const canView = canEdit

  useEffect(() => {
    if (canView) fetchGeneralSettings()
  }, [canView, fetchGeneralSettings])

  if (isLoadingGeneral) {
    return (
      <div className="flex items-center justify-center pt-16">
        <span className="text-sm text-[#535862]">Loading...</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col pt-8">
      {/* General Settings Header */}
      <div className="flex flex-col gap-1 w-full pb-5 border-b border-[#e9eaeb]">
        <h2 className="text-lg font-semibold text-[#181d27]">General Settings</h2>
        <p className="text-sm text-[#535862]">
          Configure system-wide preferences and verification rules.
        </p>
      </div>

      {/* Session Auto Timeout Row */}
      <div className="flex flex-row w-full py-5 border-b border-[#e9eaeb] items-start">
        <div className="w-[300px] shrink-0 mr-8 flex flex-col gap-1">
          <label className="text-sm font-semibold text-[#344054]">Session Auto Timeout</label>
          <p className="text-sm text-[#475467]">
            Automatically sign out users after a period of inactivity.
          </p>
        </div>
        <div className="flex-1 max-w-[400px]">
          <div className="relative flex items-center w-full rounded-lg border border-[#D0D5DD] bg-white shadow-sm overflow-hidden focus-within:ring-1 focus-within:ring-[#14378E] focus-within:border-[#14378E]">
            <input
              type="text"
              name="timeout"
              id="timeout"
              className="block w-full border-0 py-2.5 pl-3.5 pr-16 text-gray-900 ring-0 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 outline-none"
              inputMode="numeric"
              placeholder="30"
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(e.target.value)}
              disabled={!canEdit}
            />
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <span className="text-gray-500 sm:text-sm">minute</span>
            </div>
          </div>
          <p className="mt-1.5 text-sm text-[#475467]">
            Leave empty to use the default (30 minutes).
          </p>
        </div>
      </div>

      {/* Consent for candidate Row */}
      <div className="flex flex-row w-full py-5 border-b border-[#e9eaeb] items-start">
        <div className="w-[300px] shrink-0 mr-8 flex flex-col gap-1">
          <label className="text-sm font-semibold text-[#344054]">
            Consent for candidate <span className="text-[#14378E]">*</span>
          </label>
          <p className="text-sm text-[#475467]">
            Please configure the supporting data fields that candidates are required to complete
            during the application process. When a step is set to &quot;Uploaded by the HR&quot;, the
            file is supplied per candidate in New Enquiry.
          </p>
        </div>
        <div className="flex-1 max-w-[400px] flex flex-col gap-6">
          <RadioField
            label={CONSENT_SETTINGS.ektp_upload_process.label}
            name="ektp_upload_process"
            value={ektpUploadProcess}
            onChange={setEktpUploadProcess}
            disabled={!canEdit}
            options={CONSENT_SETTINGS.ektp_upload_process.options}
          />
          <RadioField
            label={CONSENT_SETTINGS.kyc_enabled.label}
            name="kyc_enabled"
            value={kycEnabled}
            onChange={setKycEnabled}
            disabled={!canEdit}
            options={CONSENT_SETTINGS.kyc_enabled.options}
          />
          <RadioField
            label={CONSENT_SETTINGS.job_application_form_mode.label}
            name="job_application_form_mode"
            value={jobApplicationFormMode}
            onChange={setJobApplicationFormMode}
            disabled={!canEdit}
            options={CONSENT_SETTINGS.job_application_form_mode.options}
          />
          <RadioField
            label={CONSENT_SETTINGS.terms_condition_mode.label}
            name="terms_condition_mode"
            value={termsConditionMode}
            onChange={setTermsConditionMode}
            disabled={!canEdit}
            options={CONSENT_SETTINGS.terms_condition_mode.options}
          />
        </div>
      </div>

      {/* Footer Buttons */}
      {canEdit && (
        <div className="flex w-full justify-end gap-3 pt-6 pb-2">
          <MyButton color="secondary" size="md" variant="outlined" onClick={cancelGeneralSettings}>
            Cancel
          </MyButton>
          <MyButton color="primary" size="md" variant="filled" onClick={updateGeneralSettings}>
            Save
          </MyButton>
        </div>
      )}
    </div>
  )
}
